import { getNinjaConfig } from '@/config/ninja';
import { getRegionConfig } from '@/config/region';
import { CUSTOM_EVENTS } from '@/const/events/customEvents';
import { AVAILABLE_TRACKERS } from '@/generated/types.generated.ts';
import { getLogger } from '@/logger';
import { getHydraDefaultParams } from '@/trackers/params/getHydraDefaultParams.ts';
import { Event, UserProps } from '@/types/events';
import { triggerCustomEvent } from '@/utils/event';
import { makeMapping, makeObjectMapping } from '@/utils/mapping';
import { normalizeEventValue } from '@/utils/string';
import { BaseTracker } from './BaseTracker';
import { initBraze } from './init-snippets/initBraze';

const logger = getLogger('BrazeTracker');

export class BrazeTracker extends BaseTracker {
  constructor() {
    super();

    this.trackerName = 'braze';
    this.scriptTagId = 'braze-script-tag';
    this.scriptUrl = 'https://js.appboycdn.com/web-sdk/5.7/braze.min.js';
    this.listeners = this.createStandardTrackingListeners();
  }

  async init() {
    try {
      if (this.isDisabled()) {
        this.state = 'disabled';
        return this;
      }

      const code = this.getToken();
      const endpoint = this.getEndpoint();
      const oldTag = document.getElementById(this.scriptTagId!);
      if (oldTag) {
        logger.debug(`${this.trackerName} already exists on the page`);
        return this;
      }

      this.state = 'loading';

      await initBraze(window, document, 'script', null, null, this.scriptUrl, this.scriptTagId);

      if (window.braze) {
        this.enableAndInitialize(code!, endpoint!);
        // TODO: remove custom callback
        if (typeof window.brazeInitCallback === 'function') {
          window.brazeInitCallback(window.braze);
        }
        triggerCustomEvent(CUSTOM_EVENTS.BRAZE_INIT, window.braze);
        logger.debug('Braze tracker initialized');
        this.state = 'ready';
      } else {
        logger.debug('window.braze is missing after init');
        this.state = 'error';
      }
    } catch (err) {
      logger.error('Braze tracker initialization error', err);
      this.state = 'error';
      throw err;
    }

    return this;
  }

  async trackEvent(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    const mappedEvent = makeMapping(this.trackerName, 'trackEvent', normalizeEventValue(e.value, 'dash'));

    if (!mappedEvent) {
      logger.debug('Values not found in the mapping. Event tracking aborted', e.value);
      return;
    }

    if (window.braze && typeof window.braze.logCustomEvent === 'function') {
      const trackParams = this.getTrackParams(e);
      const isLoggingSuccessful = window.braze.logCustomEvent(mappedEvent.value as string, trackParams);

      if (!isLoggingSuccessful) {
        logger.debug('Braze event tracking failed', e.actionType, e.value, e.props);
      } else {
        logger.debug('Braze event tracked', e.actionType, e.value, e.props);
      }
    }
  }

  async trackPage(e: Event) {
    if (!this.hasRequiredConsent()) {
      this.cleanup();
      logger.debug('Tracker disabled. Page tracking aborted', e);
      return;
    }

    const mappedPage = makeMapping(this.trackerName, 'trackPage', e.value);
    if (!mappedPage) {
      logger.debug('Values not found in the mapping. Page tracking aborted', e.value);
      return;
    }

    if (window.braze && typeof window.braze.logCustomEvent === 'function') {
      const trackParams = this.getTrackParams(e);
      const isLoggingSuccessful = window.braze.logCustomEvent(mappedPage.value, trackParams);

      if (!isLoggingSuccessful) {
        logger.debug('Braze page tracking failed', e.actionType, e.value, e.props);
      } else {
        logger.debug('Braze page tracked', e.actionType, e.value, e.props);
      }
    }
  }

  getEndpoint() {
    return getRegionConfig()?.custom?.[getNinjaConfig().siteUrl]?.config?.[AVAILABLE_TRACKERS.braze]?.endpoint;
  }

  // Internal function - enable SDK is previously disabled and initialize
  enableAndInitialize(code: string, endpoint: string) {
    if (window.braze?.isDisabled()) {
      window.braze?.enableSDK();
    }
    window.braze?.initialize(code, {
      baseUrl: endpoint,
      enableLogging: getNinjaConfig().environment !== 'production',
      doNotLoadFontAwesome: true,
      allowUserSuppliedJavascript: true,
      ...this.getAdditionalParams(),
    });
  }

  // Disable SDK
  cleanup() {
    super.cleanup();
    if (window.braze) {
      window.braze.disableSDK();
    }
  }

  private getAdditionalParams() {
    const regionConfig = getRegionConfig();
    const ninjaConfig = getNinjaConfig();
    let additional_params = regionConfig?.custom?.[ninjaConfig.siteUrl]?.config?.[AVAILABLE_TRACKERS.braze]?.additional_params || {};

    if (ninjaConfig.environment !== 'production') {
      // Allow to overwrite only on non-prod
      if (ninjaConfig?.custom?.braze?.additional_params) {
        additional_params = ninjaConfig.custom.braze.additional_params as Record<string, unknown>;
      }
    }

    return additional_params;
  }

  getTrackParams(e: Event, additionalParams: UserProps = {}): Record<string, unknown> {
    // include params_sl (if mapped)
    const moduleDefaultParams = makeObjectMapping(this.trackerName, getHydraDefaultParams(e));

    return super.getTrackParams(e, { ...additionalParams, ...moduleDefaultParams });
  }
}
